import React, { useState, useEffect, useMemo } from 'react';
import { 
    Box,
    Button,
    TextField,
    Tooltip,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
} from '@mui/material';
import {
    Add,
    Delete,
    Edit,
    PlayArrow,
    Start,
    Save,
    Cancel,
    Download,
    Share,
    SystemUpdateAlt,
    MusicNote,
    CloudUpload,
} from '@mui/icons-material';
import { AuditioBackendManager, SnippetData } from './AuditioBackend';
import { useLogging } from './contexts/LoggingContext';
import ConfirmationDialog from './components/ConfirmationDialog';
import ExportDialog from './ExportDialog';
import ImportDialogAPI from './ImportDialogAPI';
import { saveAs } from 'file-saver';
import { HHMMSSToSeconds, secondsToHHMMSS } from './TimeUtils';
import { useAuth } from './contexts/AuthContext';
import { metricsService } from './services/MetricsService';

// Base snippet interface
interface BaseSnippet {
    id: string | number;
    startTime: number;
    endTime: number;
    name: string;
    isActive?: boolean;
}

// YouTube-specific snippet interface
interface YouTubeSnippet extends BaseSnippet {
    id: number;
    audioFileId: number;
    regionId: string;
    isActive: boolean;
}

// Audio-specific snippet interface (WaveSurfer region)
interface AudioSnippet extends BaseSnippet {
    id: string;
    content: any;
    color?: string;
}

// Unified props interface
interface UnifiedSnippetsViewProps {
  auditioBackendManager: AuditioBackendManager;
  audioFileId: number;
  onSeek: (time: number) => void;
  onSnippetsChange: () => void;
  
  // YouTube-specific props (optional)
  onSnippetActivate?: (snippet: YouTubeSnippet) => void;
  onSnippetPlayFromHere?: (snippet: YouTubeSnippet) => void;
  onPlay?: () => void;
  activeSnippet?: YouTubeSnippet | null;
  
  // Audio-specific props (optional)
  regions?: any;
  wavesurferRef?: any;
  regionRef?: any;
  renderTrigger?: number;
  setRenderTrigger?: React.Dispatch<React.SetStateAction<number>>;
  waveformIsReady?: boolean;
  
  // Mode to determine behavior
  mode: 'youtube' | 'audio';
  
  // Ownership props
  ownerId?: number | null;
  ownerDisplayName?: string | null;
}

const UnifiedSnippetsView: React.FC<UnifiedSnippetsViewProps> = ({
  auditioBackendManager,
  audioFileId,
  onSeek,
  onSnippetsChange,
  onSnippetActivate,
  onSnippetPlayFromHere,
  onPlay,
  activeSnippet,
  regions,
  wavesurferRef,
  regionRef,
  renderTrigger,
  setRenderTrigger,
  waveformIsReady,
  mode,
  ownerId,
  ownerDisplayName
}) => {
    const { user, firebaseUser } = useAuth();
    const { addLog } = useLogging();
    const [snippets, setSnippets] = useState<BaseSnippet[]>([]);
    const [isAdding, setIsAdding] = useState(false);
    const [newSnippetStartTime, setNewSnippetStartTime] = useState('');
    const [newSnippetEndTime, setNewSnippetEndTime] = useState('');
    const [newSnippetName, setNewSnippetName] = useState('New Snippet');
    const [editingSnippetId, setEditingSnippetId] = useState<string | number | null>(null);
    const [editStartTime, setEditStartTime] = useState('');
    const [editEndTime, setEditEndTime] = useState('');
    const [editName, setEditName] = useState('');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [snippetToDelete, setSnippetToDelete] = useState<BaseSnippet | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    
    // Audio-specific state
    const [openExport, setOpenExport] = useState(false);
    const [openImport, setOpenImport] = useState(false);
    const [editingNameId, setEditingNameId] = useState<string>('');
    const [editingNameValue, setEditingNameValue] = useState<string>('');
    const [isSavingName, setIsSavingName] = useState(false);
    const [editingSnippet, setEditingSnippet] = useState<BaseSnippet | null>(null);

    // Check if current user is the owner or if file has no owner (always editable)
    // Files with no owner are always editable by everyone
    // Use useMemo to recompute when ownerId, ownerDisplayName, or user changes
    const isOwner = useMemo(() => {
        // If ownerDisplayName exists, the file has an owner (even if ownerId is missing due to data inconsistency)
        // Only treat as "no owner" if both ownerId and ownerDisplayName are missing
        const hasOwner = ownerId != null || ownerDisplayName != null;
        
        if (!hasOwner) {
            console.log('🔍 UnifiedSnippetsView - isOwner: File has no owner (no ownerId and no ownerDisplayName), returning true');
            return true;
        }
        
        // If we have ownerDisplayName but no ownerId, try to match by display name as fallback (case-insensitive)
        if (ownerId === null || ownerId === undefined) {
            if (ownerDisplayName && user?.displayName) {
                const displayNameMatch = user.displayName.toLowerCase() === ownerDisplayName.toLowerCase();
                console.log('🔍 UnifiedSnippetsView - isOwner: File has ownerDisplayName but no ownerId - checking display name match:', {
                    userDisplayName: user.displayName,
                    ownerDisplayName,
                    displayNameMatch
                });
                return displayNameMatch;
            }
            console.log('🔍 UnifiedSnippetsView - isOwner: File has ownerDisplayName but no ownerId and no display name match - denying access for safety');
            return false;
        }
        
        // Otherwise, check if current user is the owner
        const userId = user?.id;
        const fileOwnerId = ownerId;
        const isOwnerResult = userId !== undefined && userId !== null && Number(userId) === Number(fileOwnerId);
        
        console.log('🔍 UnifiedSnippetsView - isOwner check:', {
            userId,
            userIdType: typeof userId,
            fileOwnerId,
            fileOwnerIdType: typeof fileOwnerId,
            isOwnerResult,
            userExists: !!user,
            ownerDisplayName,
            hasOwner
        });
        
        return isOwnerResult;
    }, [ownerId, ownerDisplayName, user?.id, user?.displayName]);

    // Load snippets when component mounts or audioFileId changes
    useEffect(() => {
        if (mode === 'youtube') {
            loadYouTubeSnippets();
        } else {
            // Audio snippets are loaded through regions prop
            updateAudioSnippets();
        }
    }, [audioFileId, mode]);

    // Refresh snippets when refreshTrigger changes or renderTrigger changes (for YouTube mode)
    useEffect(() => {
        if (refreshTrigger > 0 || (mode === 'youtube' && renderTrigger && renderTrigger > 0)) {
            if (mode === 'youtube') {
                loadYouTubeSnippets();
            } else {
                updateAudioSnippets();
            }
        }
    }, [refreshTrigger, renderTrigger, mode]);

    // Update audio snippets when regions change
    useEffect(() => {
        if (mode === 'audio' && regions?.current) {
            updateAudioSnippets();
        }
    }, [regions?.current, renderTrigger]);

    const updateAudioSnippets = () => {
        if (!regions?.current) return;
        
        const currentRegions: AudioSnippet[] = [];
        Object.entries(regions.current).forEach(([id, region]: [string, any]) => {
            currentRegions.push({
                id: region.id,
                startTime: region.start,
                endTime: region.end,
                name: typeof region.content === 'string' ? region.content : region.content?.innerText || '',
                content: region.content,
                color: region.color,
                isActive: regionRef?.current?.id === region.id
            });
        });
        
        // Sort regions by start time (ascending)
        setSnippets(currentRegions.sort((a, b) => a.startTime - b.startTime));
    };

    const loadYouTubeSnippets = async () => {
        try {
            const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets/${audioFileId}`);
            if (response.ok) {
                const data = await response.json();
                setSnippets(data);
            }
        } catch (error) {
            console.error('Error loading snippets:', error);
        }
    };

    // Create a wrapper for onSnippetsChange that also triggers refresh
    const handleSnippetsChange = () => {
        onSnippetsChange();
        if (mode === 'youtube') {
            loadYouTubeSnippets();
        }
        setRefreshTrigger(prev => prev + 1);
    };

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const parseTime = (timeString: string): number => {
        const parts = timeString.split(':');
        if (parts.length === 2) {
            return parseInt(parts[0]) * 60 + parseInt(parts[1]);
        }
        return 0;
    };

    const handleAddSnippet = async () => {
        if (!newSnippetStartTime || !newSnippetEndTime) {
            alert('Please enter both start and end times');
            return;
        }

        const startTime = mode === 'youtube' ? parseTime(newSnippetStartTime) : HHMMSSToSeconds(newSnippetStartTime);
        const endTime = mode === 'youtube' ? parseTime(newSnippetEndTime) : HHMMSSToSeconds(newSnippetEndTime);

        if (startTime >= endTime) {
            alert('Start time must be before end time');
            return;
        }

        if (mode === 'youtube') {
            await handleAddYouTubeSnippet(startTime, endTime);
        } else {
            await handleAddAudioSnippet(startTime, endTime);
        }
    };

    const handleAddYouTubeSnippet = async (startTime: number, endTime: number) => {
        try {
            const snippet = {
                audioFileId: audioFileId,
                regionId: `snippet_${Date.now()}`,
                startTime: startTime,
                endTime: endTime,
                name: newSnippetName,
                isActive: false
            };

            const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ snippet }),
            });

            if (response.ok) {
                resetAddForm();
                loadYouTubeSnippets();
                handleSnippetsChange();
            }
        } catch (error) {
            console.error('Error adding snippet:', error);
        }
    };

    const handleAddAudioSnippet = async (startTime: number, endTime: number) => {
        if (wavesurferRef?.current) {
            const RegionsPlugin = (await import('wavesurfer.js/plugins/regions')).default;
            const newRegion = (wavesurferRef.current.getActivePlugins()[1] as any).addRegion({
                start: startTime,
                end: endTime,
                content: newSnippetName,
                contentEditable: true,
                drag: true,
                resize: true,
            });

            // Store the region in regions.current
            regions.current = {
                ...regions.current,
                [newRegion.id]: newRegion
            };

            // Save the new region to the backend
            await auditioBackendManager.addOrUpdateSnippetFromRegion(newRegion, audioFileId);
            await auditioBackendManager.updateActiveRegion(regionRef, newRegion.id, regions, audioFileId);
        }

        resetAddForm();
        setRenderTrigger?.(prev => prev + 1);
    };

    const resetAddForm = () => {
        setIsAdding(false);
        setNewSnippetStartTime('');
        setNewSnippetEndTime('');
        setNewSnippetName('New Snippet');
    };

    const handleEditSnippet = async (snippet: BaseSnippet) => {
        if (!editStartTime || !editEndTime) {
            alert('Please enter both start and end times');
            return;
        }

        const startTime = mode === 'youtube' ? parseTime(editStartTime) : HHMMSSToSeconds(editStartTime);
        const endTime = mode === 'youtube' ? parseTime(editEndTime) : HHMMSSToSeconds(editEndTime);

        if (startTime >= endTime) {
            alert('Start time must be before end time');
            return;
        }

        if (mode === 'youtube') {
            await handleEditYouTubeSnippet(snippet as YouTubeSnippet, startTime, endTime);
        } else {
            await handleEditAudioSnippet(snippet as AudioSnippet, startTime, endTime);
        }
    };

    const handleEditYouTubeSnippet = async (snippet: YouTubeSnippet, startTime: number, endTime: number) => {
        try {
            const updatedSnippet = {
                ...snippet,
                startTime: startTime,
                endTime: endTime,
                name: editName,
            };

            const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ snippet: updatedSnippet }),
            });

            if (response.ok) {
                setEditingSnippetId(null);
                loadYouTubeSnippets();
                handleSnippetsChange();
            }
        } catch (error) {
            console.error('Error updating snippet:', error);
        }
    };

    const handleEditAudioSnippet = async (snippet: AudioSnippet, startTime: number, endTime: number) => {
        if (regions?.current?.[snippet.id]) {
            // Update the active region
            await auditioBackendManager.updateActiveRegion(regionRef, snippet.id, regions, audioFileId);
            
            // Update the region options
            regions.current[snippet.id].setOptions({
                start: startTime,
                end: endTime,
                content: editName,
            });

            // Save the snippet
            await auditioBackendManager.addOrUpdateSnippetFromRegion(regions.current[snippet.id], audioFileId);
        }

        setEditingSnippetId(null);
        setRenderTrigger?.(prev => prev + 1);
    };

    const handleDeleteSnippet = (snippet: BaseSnippet) => {
        setSnippetToDelete(snippet);
        setShowDeleteDialog(true);
    };

    const confirmDeleteSnippet = async () => {
        if (!snippetToDelete) return;

        if (mode === 'youtube') {
            await handleDeleteYouTubeSnippet(snippetToDelete as YouTubeSnippet);
        } else {
            await handleDeleteAudioSnippet(snippetToDelete as AudioSnippet);
        }
    };

    const handleDeleteYouTubeSnippet = async (snippet: YouTubeSnippet) => {
        try {
            const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    audioFileId: snippet.audioFileId,
                    regionId: snippet.regionId,
                }),
            });

            if (response.ok) {
                loadYouTubeSnippets();
                handleSnippetsChange();
            }
        } catch (error) {
            console.error('Error deleting snippet:', error);
        } finally {
            setShowDeleteDialog(false);
            setSnippetToDelete(null);
        }
    };

    const handleDeleteAudioSnippet = async (snippet: AudioSnippet) => {
        await auditioBackendManager.deleteSnippet(snippet.id, regions, regionRef, audioFileId);
        setRenderTrigger?.(prev => prev + 1);
        setShowDeleteDialog(false);
        setSnippetToDelete(null);
    };

    const startEdit = (snippet: BaseSnippet) => {
        setEditingSnippetId(snippet.id);
        setEditStartTime(mode === 'youtube' ? formatTime(snippet.startTime) : secondsToHHMMSS(snippet.startTime));
        setEditEndTime(mode === 'youtube' ? formatTime(snippet.endTime) : secondsToHHMMSS(snippet.endTime));
        setEditName(snippet.name);
    };

    const cancelEdit = () => {
        setEditingSnippetId(null);
        setEditStartTime('');
        setEditEndTime('');
        setEditName('');
    };

    const handlePlaySnippet = async (snippet: BaseSnippet) => {
        addLog('🎵 Snippet play initiated', {
            snippetId: snippet.id,
            snippetName: snippet.name,
            startTime: snippet.startTime,
            endTime: snippet.endTime,
            mode,
            audioFileId
        });

        // Note: Snippet playback tracking is now handled by region-in event in WaveformAPI
        // This covers both initial play and loops automatically

        onSeek(snippet.startTime);
        if (mode === 'youtube' && onSnippetActivate) {
            addLog('📺 YouTube mode: Activating snippet', { snippetId: snippet.id });
            onSnippetActivate(snippet as YouTubeSnippet);
            return;
        }
        
        if (mode === 'audio' && regions?.current?.[snippet.id]) {
            try {
                // Check if wavesurfer is ready
                const isWavesurferReady = wavesurferRef?.current && 
                    typeof wavesurferRef.current.getDuration === 'function' &&
                    wavesurferRef.current.getDuration() > 0;

                addLog('🔍 Checking wavesurfer readiness', {
                    hasWavesurferRef: !!wavesurferRef?.current,
                    isWavesurferReady,
                    hasGetDuration: wavesurferRef?.current && typeof wavesurferRef.current.getDuration === 'function',
                    duration: wavesurferRef?.current && typeof wavesurferRef.current.getDuration === 'function' 
                        ? wavesurferRef.current.getDuration() 
                        : 'N/A'
                });

                // If not ready, seek to snippet start time first (may trigger loading on mobile)
                if (!isWavesurferReady && wavesurferRef?.current) {
                    addLog('⚠️ Wavesurfer not ready, seeking to snippet start time', { 
                        snippetStartTime: snippet.startTime 
                    });
                    try {
                        if (typeof wavesurferRef.current.seekTo === 'function') {
                            wavesurferRef.current.seekTo(snippet.startTime);
                            addLog('✅ Seeked to snippet start time', { snippetStartTime: snippet.startTime });
                        } else {
                            addLog('❌ seekTo function not available', {});
                        }
                    } catch (seekError) {
                        addLog('❌ Error seeking to snippet start time', { 
                            error: seekError instanceof Error ? seekError.message : String(seekError),
                            stack: seekError instanceof Error ? seekError.stack : undefined
                        });
                    }
                }

                // Load snippet for audio mode
                addLog('🔄 Updating active region', { 
                    snippetId: snippet.id,
                    regionId: String(snippet.id)
                });
                await auditioBackendManager.updateActiveRegion(regionRef, String(snippet.id), regions, audioFileId);
                
                // Verify region is set correctly
                if (!regionRef?.current) {
                    addLog('❌ Region not set after updateActiveRegion', {
                        snippetId: snippet.id,
                        regionRefExists: !!regionRef,
                        regionRefCurrent: !!regionRef?.current
                    });
                    return;
                }
                addLog('✅ Region set successfully', {
                    snippetId: snippet.id,
                    regionId: regionRef.current.id,
                    hasPlayMethod: typeof regionRef.current.play === 'function'
                });

                // Simple approach: Play snippet based on device type
                if (wavesurferRef?.current && regionRef?.current) {
                    try {
                        // Detect mobile device
                        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                            (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
                            ('ontouchstart' in window);
                        
                        addLog('🎵 Playing snippet', {
                            isMobile,
                            snippetStartTime: snippet.startTime,
                            hasRegionRef: !!regionRef.current,
                            hasPlayMethod: typeof regionRef.current.play === 'function'
                        });
                        
                        if (isMobile) {
                            // Mobile: Resume AudioContext, play main wavesurfer, then region.play()
                            if (wavesurferRef.current.backend) {
                                try {
                                    let audioContext: AudioContext | null = null;
                                    if (wavesurferRef.current.backend.ac) {
                                        audioContext = wavesurferRef.current.backend.ac;
                                    } else if (typeof wavesurferRef.current.backend.getAudioContext === 'function') {
                                        audioContext = wavesurferRef.current.backend.getAudioContext();
                                    }
                                    
                                    if (audioContext && audioContext.state === 'suspended') {
                                        await audioContext.resume();
                                        addLog('✅ Mobile: AudioContext resumed', {});
                                    }
                                } catch (error) {
                                    addLog('⚠️ Mobile: AudioContext resume error', {
                                        error: error instanceof Error ? error.message : String(error)
                                    });
                                }
                            }
                            
                            // Play main wavesurfer to unlock audio
                            if (typeof wavesurferRef.current.play === 'function') {
                                const playResult = wavesurferRef.current.play();
                                if (playResult && typeof playResult.then === 'function') {
                                    await playResult;
                                }
                                await new Promise(resolve => setTimeout(resolve, 100));
                                
                                // Seek to snippet start while playing
                                if (typeof wavesurferRef.current.seekTo === 'function') {
                                    wavesurferRef.current.seekTo(snippet.startTime);
                                    await new Promise(resolve => setTimeout(resolve, 100));
                                }
                                
                                // Now play the region
                                if (regionRef.current && typeof regionRef.current.play === 'function') {
                                    regionRef.current.play();
                                    addLog('✅ Mobile: region.play() called', {});
                                }
                            }
                        } else {
                            // Desktop: Just call region.play() directly
                            if (regionRef.current && typeof regionRef.current.play === 'function') {
                                regionRef.current.play();
                                addLog('✅ Desktop: region.play() called', {});
                            }
                        }
                    } catch (error) {
                        addLog('❌ Error playing snippet', {
                            error: error instanceof Error ? error.message : String(error)
                        });
                    }
                } else {
                    addLog('❌ Cannot play snippet: missing wavesurfer or region', {
                        hasWavesurferRef: !!wavesurferRef?.current,
                        hasRegionRef: !!regionRef?.current
                    });
                }
                
                // Only set loop option to "snippet" if it's not already set
                // This prevents overriding the active snippet that was just set
                const currentLoopOption = (document.querySelector('input[name="loopOption"]:checked') as HTMLInputElement)?.value;
                addLog('🔄 Checking loop option', {
                    currentLoopOption,
                    willSetToSnippet: currentLoopOption !== 'snippet'
                });
                if (currentLoopOption !== 'snippet') {
                    const loopChangeEvent = new CustomEvent('setLoopOption', {
                        detail: { loopOption: 'snippet' }
                    });
                    window.dispatchEvent(loopChangeEvent);
                    addLog('✅ Set loop option to snippet', {});
                } else {
                    addLog('ℹ️ Loop option already set to snippet', {});
                }
                
                // Trigger re-render to update UI
                setRenderTrigger?.(prev => prev + 1);
                addLog('✅ handlePlaySnippet completed successfully', {
                    snippetId: snippet.id,
                    snippetName: snippet.name
                });
            } catch (error) {
                addLog('❌ Fatal error in handlePlaySnippet', {
                    error: error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined,
                    errorType: error instanceof Error ? error.constructor.name : typeof error,
                    snippetId: snippet.id,
                    snippetName: snippet.name
                });
                console.error('❌ Error in handlePlaySnippet:', error);
                // Ensure handler doesn't enter broken state
                setRenderTrigger?.(prev => prev + 1);
            }
        } else {
            addLog('⚠️ Cannot play snippet: conditions not met', {
                mode,
                hasRegions: !!regions?.current,
                hasSnippetInRegions: regions?.current && !!regions.current[snippet.id],
                snippetId: snippet.id
            });
        }
        onPlay?.();
    };

    const handlePlaySnippetFromHere = async (snippet: BaseSnippet) => {
        addLog('🎵 Snippet play from here initiated', {
            snippetId: snippet.id,
            snippetName: snippet.name,
            startTime: snippet.startTime,
            endTime: snippet.endTime,
            mode,
            audioFileId
        });

        onSeek(snippet.startTime);
        if (mode === 'youtube' && onSnippetPlayFromHere) {
            addLog('📺 YouTube mode: Playing snippet from here (no loop)', { snippetId: snippet.id });
            onSnippetPlayFromHere(snippet as YouTubeSnippet);
            return;
        }
        
        if (mode === 'audio' && regions?.current?.[snippet.id]) {
            try {
                // Check if wavesurfer is ready
                const isWavesurferReady = wavesurferRef?.current && 
                    typeof wavesurferRef.current.getDuration === 'function' &&
                    wavesurferRef.current.getDuration() > 0;

                addLog('🔍 Checking wavesurfer readiness', {
                    hasWavesurferRef: !!wavesurferRef?.current,
                    isWavesurferReady,
                    hasGetDuration: wavesurferRef?.current && typeof wavesurferRef.current.getDuration === 'function',
                    duration: wavesurferRef?.current && typeof wavesurferRef.current.getDuration === 'function' 
                        ? wavesurferRef.current.getDuration() 
                        : 'N/A'
                });

                // If not ready, seek to snippet start time first (may trigger loading on mobile)
                if (!isWavesurferReady && wavesurferRef?.current) {
                    addLog('⚠️ Wavesurfer not ready, seeking to snippet start time', { 
                        snippetStartTime: snippet.startTime 
                    });
                    try {
                        if (typeof wavesurferRef.current.seekTo === 'function') {
                            wavesurferRef.current.seekTo(snippet.startTime);
                            addLog('✅ Seeked to snippet start time', { snippetStartTime: snippet.startTime });
                        } else {
                            addLog('❌ seekTo function not available', {});
                        }
                    } catch (seekError) {
                        addLog('❌ Error seeking to snippet start time', { 
                            error: seekError instanceof Error ? seekError.message : String(seekError),
                            stack: seekError instanceof Error ? seekError.stack : undefined
                        });
                    }
                }

                // Load snippet for audio mode
                addLog('🔄 Updating active region', { 
                    snippetId: snippet.id,
                    regionId: String(snippet.id)
                });
                await auditioBackendManager.updateActiveRegion(regionRef, String(snippet.id), regions, audioFileId);
                
                // Verify region is set correctly
                if (!regionRef?.current) {
                    addLog('❌ Region not set after updateActiveRegion', {
                        snippetId: snippet.id,
                        regionRefExists: !!regionRef,
                        regionRefCurrent: !!regionRef?.current
                    });
                    return;
                }
                addLog('✅ Region set successfully', {
                    snippetId: snippet.id,
                    regionId: regionRef.current.id,
                    hasPlayMethod: typeof regionRef.current.play === 'function'
                });

                // Simple approach: Play snippet based on device type
                if (wavesurferRef?.current && regionRef?.current) {
                    try {
                        // Detect mobile device
                        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                            (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
                            ('ontouchstart' in window);
                        
                        addLog('🎵 Playing snippet from here', {
                            isMobile,
                            snippetStartTime: snippet.startTime,
                            hasRegionRef: !!regionRef.current,
                            hasPlayMethod: typeof regionRef.current.play === 'function'
                        });
                        
                        if (isMobile) {
                            // Mobile: Resume AudioContext, play main wavesurfer, then region.play()
                            if (wavesurferRef.current.backend) {
                                try {
                                    let audioContext: AudioContext | null = null;
                                    if (wavesurferRef.current.backend.ac) {
                                        audioContext = wavesurferRef.current.backend.ac;
                                    } else if (typeof wavesurferRef.current.backend.getAudioContext === 'function') {
                                        audioContext = wavesurferRef.current.backend.getAudioContext();
                                    }
                                    
                                    if (audioContext && audioContext.state === 'suspended') {
                                        await audioContext.resume();
                                        addLog('✅ Mobile: AudioContext resumed', {});
                                    }
                                } catch (error) {
                                    addLog('⚠️ Mobile: AudioContext resume error', {
                                        error: error instanceof Error ? error.message : String(error)
                                    });
                                }
                            }
                            
                            // Play main wavesurfer to unlock audio
                            if (typeof wavesurferRef.current.play === 'function') {
                                const playResult = wavesurferRef.current.play();
                                if (playResult && typeof playResult.then === 'function') {
                                    await playResult;
                                }
                                await new Promise(resolve => setTimeout(resolve, 100));
                                
                                // Seek to snippet start while playing
                                if (typeof wavesurferRef.current.seekTo === 'function') {
                                    wavesurferRef.current.seekTo(snippet.startTime);
                                    await new Promise(resolve => setTimeout(resolve, 100));
                                }
                                
                                // Now play the region
                                if (regionRef.current && typeof regionRef.current.play === 'function') {
                                    regionRef.current.play();
                                    addLog('✅ Mobile: region.play() called', {});
                                }
                            }
                        } else {
                            // Desktop: Just call region.play() directly
                            if (regionRef.current && typeof regionRef.current.play === 'function') {
                                regionRef.current.play();
                                addLog('✅ Desktop: region.play() called', {});
                            }
                        }
                    } catch (error) {
                        addLog('❌ Error playing snippet from here', {
                            error: error instanceof Error ? error.message : String(error)
                        });
                    }
                } else {
                    addLog('❌ Cannot play snippet from here: missing wavesurfer or region', {
                        hasWavesurferRef: !!wavesurferRef?.current,
                        hasRegionRef: !!regionRef?.current
                    });
                }
                
                // Set loop option to "none" (empty string) for play from here
                const loopChangeEvent = new CustomEvent('setLoopOption', {
                    detail: { loopOption: '' }
                });
                window.dispatchEvent(loopChangeEvent);
                addLog('✅ Set loop option to none (play from here)', {});
                
                // Trigger re-render to update UI
                setRenderTrigger?.(prev => prev + 1);
                addLog('✅ handlePlaySnippetFromHere completed successfully', {
                    snippetId: snippet.id,
                    snippetName: snippet.name
                });
            } catch (error) {
                addLog('❌ Fatal error in handlePlaySnippetFromHere', {
                    error: error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined,
                    errorType: error instanceof Error ? error.constructor.name : typeof error,
                    snippetId: snippet.id,
                    snippetName: snippet.name
                });
                console.error('❌ Error in handlePlaySnippetFromHere:', error);
                // Ensure handler doesn't enter broken state
                setRenderTrigger?.(prev => prev + 1);
            }
        } else {
            addLog('⚠️ Cannot play snippet from here: conditions not met', {
                mode,
                hasRegions: !!regions?.current,
                hasSnippetInRegions: regions?.current && !!regions.current[snippet.id],
                snippetId: snippet.id
            });
        }
        onPlay?.();
    };

    // Audio-specific functions
    const handleFFmpegTrim = async (snippet: AudioSnippet) => {
        console.log('🎬 FRONTEND: Trim button clicked!', {
            snippetId: snippet.id,
            snippetName: snippet.name,
            audioFileId: audioFileId,
            startTime: snippet.startTime,
            endTime: snippet.endTime,
            timestamp: new Date().toISOString()
        });
        
        try {
            console.log('🎬 FRONTEND: Calling auditioBackendManager.trimSnippet...');
            const trimmedBlob = await auditioBackendManager.trimSnippet(snippet.id, regions, audioFileId);
            console.log('🎬 FRONTEND: trimSnippet completed successfully, blob size:', trimmedBlob.size);
            
            const outputFileName = `${snippet.name}.mp3`;
            console.log('🎬 FRONTEND: Starting file download with filename:', outputFileName);
            saveAs(trimmedBlob, outputFileName);
            console.log('🎬 FRONTEND: File download initiated successfully');
        } catch (error) {
            console.error('❌ FRONTEND: Error trimming snippet:', error);
            console.error('❌ FRONTEND: Error details:', {
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : undefined,
                snippetId: snippet.id,
                audioFileId: audioFileId
            });
            alert(`Failed to trim snippet: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const startNameEdit = (snippet: BaseSnippet) => {
        const snippetId = String(snippet.id);
        console.log('📝 startNameEdit called:', {
            snippetId,
            snippetName: snippet.name,
            fullSnippet: snippet,
            snippetsArrayLength: snippets.length,
            snippetsArray: snippets,
            mode,
            timestamp: new Date().toISOString()
        });
        
        setEditingNameId(snippetId);
        setEditingNameValue(snippet.name);
        setEditingSnippet(snippet);
    };

    const saveNameEdit = async (snippetId: string | number) => {
        if (isSavingName) return; // Prevent double saves
        
        // Normalize snippetId to string for consistent comparisons
        const normalizedSnippetId = String(snippetId);
        
        console.log('💾 saveNameEdit called:', {
            snippetId: normalizedSnippetId,
            originalSnippetId: snippetId,
            snippetIdType: typeof snippetId,
            editingNameValue,
            snippetsArrayLength: snippets.length,
            snippetsArray: snippets,
            editingSnippet: editingSnippet,
            mode,
            timestamp: new Date().toISOString()
        });
        
        setIsSavingName(true);
        try {
            // Try to use stored snippet first (avoids stale closure issues)
            let snippetToUpdate: BaseSnippet | undefined = editingSnippet || undefined;
            let snippetSource = 'stored';
            
            // Fallback to array search if stored snippet is missing or ID doesn't match
            if (!snippetToUpdate || String(snippetToUpdate.id) !== normalizedSnippetId) {
                console.log('⚠️ Stored snippet not available or ID mismatch, searching array:', {
                    hasStoredSnippet: !!editingSnippet,
                    storedSnippetId: editingSnippet ? String(editingSnippet.id) : 'none',
                    searchingForId: normalizedSnippetId,
                    originalSnippetId: snippetId,
                    snippetsArrayLength: snippets.length
                });
                
                snippetToUpdate = snippets.find(s => String(s.id) === normalizedSnippetId);
                snippetSource = 'array';
            }
            
            if (!snippetToUpdate) {
                console.error('❌ Snippet not found for name edit:', {
                    snippetId: normalizedSnippetId,
                    originalSnippetId: snippetId,
                    snippetsArrayLength: snippets.length,
                    snippetsArray: snippets,
                    editingSnippet: editingSnippet,
                    mode,
                    timestamp: new Date().toISOString()
                });
                return;
            }
            
            console.log('✅ Snippet found for update:', {
                snippetId: normalizedSnippetId,
                snippetName: snippetToUpdate.name,
                newName: editingNameValue,
                snippetSource,
                fullSnippet: snippetToUpdate,
                mode
            });

            if (mode === 'youtube') {
                // For YouTube snippets, use the updateSnippet method
                const updatedSnippet = {
                    ...snippetToUpdate,
                    name: editingNameValue
                } as SnippetData;

                console.log('📤 Sending snippet update to backend:', updatedSnippet);
                await auditioBackendManager.updateSnippet(updatedSnippet);
                console.log('✅ Snippet update successful, reloading snippets');
                loadYouTubeSnippets();
            } else {
                // For audio snippets, use the region-based approach
                if (regions?.current?.[normalizedSnippetId]) {
                    // Update the region's content with the new name
                    regions.current[normalizedSnippetId].setOptions({
                        content: editingNameValue,
                    });

                    // Save the snippet to the backend using the region
                    await auditioBackendManager.addOrUpdateSnippetFromRegion(regions.current[normalizedSnippetId], audioFileId);
                }
                updateAudioSnippets();
            }
            
            // Exit edit mode
            setEditingNameId('');
            setEditingNameValue('');
            setEditingSnippet(null);
            
            onSnippetsChange();
        } catch (error) {
            console.error('❌ Error saving snippet name edit:', {
                error,
                snippetId: normalizedSnippetId,
                originalSnippetId: snippetId,
                editingNameValue,
                snippetsArrayLength: snippets.length,
                editingSnippet: editingSnippet,
                mode,
                timestamp: new Date().toISOString()
            });
            // Revert to original name on error
            const originalSnippet = editingSnippet || snippets.find(s => String(s.id) === normalizedSnippetId);
            if (originalSnippet) {
                setEditingNameValue(originalSnippet.name);
            }
        } finally {
            setIsSavingName(false);
        }
    };

    const cancelNameEdit = () => {
        setEditingNameId('');
        setEditingNameValue('');
        setEditingSnippet(null);
    };

    const handleNameEditKeyDown = (event: React.KeyboardEvent, snippetId: string) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            saveNameEdit(snippetId);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            cancelNameEdit();
        }
    };

    const getRowColor = (snippet: AudioSnippet) => {
        if (snippet.color === 'rgba(243, 160, 173, 0.3)') {
            return 'rgba(243, 160, 173, 0.3)';
        }
        return 'rgba(0, 0, 0, 0)';
    };

    return (
        <Box sx={{ mt: 3, width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Paper className="snippets-paper" sx={{ width: '100%' }}>
                <Box className="snippets-header">
                    <Typography variant="h6" className="snippets-title">
                        Snippets
                    </Typography>
                    <Box className="snippets-actions">
                        <button className="snippet-action-btn" onClick={() => setOpenExport(true)}>
                            <Share />
                            <span className="tooltip">Share</span>
                        </button>
                        {isOwner && (
                            <>
                                <button className="snippet-action-btn" onClick={() => setOpenImport(true)}>
                                    <SystemUpdateAlt />
                                    <span className="tooltip">Import</span>
                                </button>
                                <button className="snippet-action-btn" onClick={() => setIsAdding(true)}>
                                    <Add />
                                    <span className="tooltip">New Snippet</span>
                                </button>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Add new snippet form */}
                {isAdding && (
                    <Box sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>Add New Snippet</Typography>
                        <TableContainer>
                            <Table className="snippets-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ textAlign: 'center' }}></TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Start Time</TableCell>
                                        <TableCell>End Time</TableCell>
                                        <TableCell>Duration</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ textAlign: 'center' }}></TableCell>
                                        <TableCell>
                                            <TextField
                                                variant="standard"
                                                value={newSnippetName}
                                                onChange={(e) => setNewSnippetName(e.target.value)}
                                                size="small"
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                variant="standard"
                                                value={newSnippetStartTime}
                                                onChange={(e) => setNewSnippetStartTime(e.target.value)}
                                                placeholder="1:30"
                                                size="small"
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                variant="standard"
                                                value={newSnippetEndTime}
                                                onChange={(e) => setNewSnippetEndTime(e.target.value)}
                                                placeholder="2:45"
                                                size="small"
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {/* Duration will be calculated and displayed */}
                                            {newSnippetStartTime && newSnippetEndTime ? 
                                                (mode === 'youtube' 
                                                    ? formatTime(parseTime(newSnippetEndTime) - parseTime(newSnippetStartTime))
                                                    : secondsToHHMMSS(HHMMSSToSeconds(newSnippetEndTime) - HHMMSSToSeconds(newSnippetStartTime))
                                                ) : '--'
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <div className="snippet-actions">
                                                <Tooltip title="Save">
                                                    <IconButton 
                                                        aria-label="save"
                                                        className="snippet-action-icon edit"
                                                        onClick={handleAddSnippet}
                                                    >
                                                        <Save />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Cancel">
                                                    <IconButton 
                                                        aria-label="cancel"
                                                        className="snippet-action-icon delete"
                                                        onClick={resetAddForm}
                                                    >
                                                        <Cancel />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* Show snippets table or empty state */}
                {snippets.length > 0 ? (
                    <TableContainer className="snippets-table-container">
                        <Table 
                            className="snippets-table"
                            sx={{
                                minWidth: '600px',
                                '& .MuiTableCell-root': {
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ textAlign: 'center' }}></TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Start Time</TableCell>
                                    <TableCell>End Time</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {snippets.map((snippet) => (
                                    <TableRow 
                                        key={String(snippet.id)}
                                        className={
                                            (mode === 'youtube' && activeSnippet?.id === snippet.id) ||
                                            (mode === 'audio' && regionRef?.current?.id === snippet.id)
                                                ? 'active-region-row' 
                                                : ''
                                        }
                                        onClick={(e) => {
                                            // Don't activate snippet if we're editing the name or the full snippet
                                            if (String(editingNameId) === String(snippet.id) || editingSnippetId === snippet.id) {
                                                return;
                                            }
                                            
                                            if (mode === 'youtube' && onSnippetActivate) {
                                                onSnippetActivate(snippet as YouTubeSnippet);
                                            } else if (mode === 'audio') {
                                                handlePlaySnippet(snippet);
                                            }
                                        }}
                                        sx={mode === 'audio' ? { backgroundColor: getRowColor(snippet as AudioSnippet) } : {}}
                                    >
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <Tooltip title="Play snippet">
                                                <IconButton 
                                                    aria-label="play"
                                                    className="snippet-action-icon load"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePlaySnippet(snippet);
                                                    }}
                                                >
                                                    <PlayArrow />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Play from here (no loop)">
                                                <IconButton 
                                                    aria-label="play from here"
                                                    className="snippet-action-icon load"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePlaySnippetFromHere(snippet);
                                                    }}
                                                    sx={{ 
                                                        ml: 0.5,
                                                        '& svg': {
                                                            fill: 'currentColor',
                                                            stroke: 'currentColor',
                                                            strokeWidth: 0,
                                                            '& path': {
                                                                fill: 'currentColor',
                                                                stroke: 'none'
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <Start />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {editingSnippetId === snippet.id ? (
                                                <TextField
                                                    variant="standard"
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    size="small"
                                                />
                                            ) : String(editingNameId) === String(snippet.id) ? (
                                                <TextField
                                                    variant="standard"
                                                    value={editingNameValue}
                                                    onChange={(e) => setEditingNameValue(e.target.value)}
                                                    onKeyDown={(e) => handleNameEditKeyDown(e, snippet.id as string)}
                                                    onBlur={() => {
                                                        if (!isSavingName) {
                                                            saveNameEdit(snippet.id as string);
                                                        }
                                                    }}
                                                    size="small"
                                                    autoFocus
                                                />
                                            ) : (
                                                <div className="snippet-name">
                                                    <span 
                                                        className="snippet-name-editable"
                                                        onClick={isOwner ? (e) => {
                                                            e.stopPropagation();
                                                            startNameEdit(snippet);
                                                        } : undefined}
                                                        style={!isOwner ? { cursor: 'default', pointerEvents: 'none' } : {}}
                                                    >
                                                        {snippet.name}
                                                    </span>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingSnippetId === snippet.id ? (
                                                <TextField
                                                    variant="standard"
                                                    value={editStartTime}
                                                    onChange={(e) => setEditStartTime(e.target.value)}
                                                    placeholder="MM:SS"
                                                    size="small"
                                                />
                                            ) : (
                                                mode === 'youtube' 
                                                    ? formatTime(snippet.startTime)
                                                    : secondsToHHMMSS(snippet.startTime)
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingSnippetId === snippet.id ? (
                                                <TextField
                                                    variant="standard"
                                                    value={editEndTime}
                                                    onChange={(e) => setEditEndTime(e.target.value)}
                                                    placeholder="MM:SS"
                                                    size="small"
                                                />
                                            ) : (
                                                mode === 'youtube' 
                                                    ? formatTime(snippet.endTime)
                                                    : secondsToHHMMSS(snippet.endTime)
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {mode === 'youtube' 
                                                ? formatTime(snippet.endTime - snippet.startTime)
                                                : secondsToHHMMSS(snippet.endTime - snippet.startTime)
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <div className="snippet-actions">
                                                {editingSnippetId === snippet.id || editingNameId === snippet.id ? (
                                                    <>
                                                        <Tooltip title="Save">
                                                            <IconButton 
                                                                aria-label="save"
                                                                className="snippet-action-icon edit"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (editingSnippetId === snippet.id) {
                                                                        handleEditSnippet(snippet);
                                                                    } else {
                                                                        saveNameEdit(snippet.id as string);
                                                                    }
                                                                }}
                                                            >
                                                                <Save />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Cancel">
                                                            <IconButton 
                                                                aria-label="cancel"
                                                                className="snippet-action-icon delete"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (editingSnippetId === snippet.id) {
                                                                        cancelEdit();
                                                                    } else {
                                                                        cancelNameEdit();
                                                                    }
                                                                }}
                                                            >
                                                                <Cancel />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <>
                                                        {isOwner && (
                                                            <>
                                                                <Tooltip title="Edit">
                                                                    <IconButton 
                                                                        aria-label="edit"
                                                                        className="snippet-action-icon edit"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            startEdit(snippet);
                                                                        }}
                                                                    >
                                                                        <Edit />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Delete">
                                                                    <IconButton 
                                                                        aria-label="delete"
                                                                        className="snippet-action-icon delete"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteSnippet(snippet);
                                                                        }}
                                                                    >
                                                                        <Delete />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                        {/* Download button - available to everyone (read-only mode) */}
                                                        {mode === 'audio' && (
                                                            <Tooltip title="Download">
                                                                <IconButton 
                                                                    aria-label="download"
                                                                    className="snippet-action-icon trim"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleFFmpegTrim(snippet as AudioSnippet);
                                                                    }}
                                                                >
                                                                    <Download />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : !isAdding && (
                    <Box className="empty-state">
                        <div className="empty-state-icon">
                            <svg viewBox="0 0 24 24">
                                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6zm-2 16c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                            </svg>
                        </div>
                        <h3 className="empty-state-title">No Snippets Yet</h3>
                        <p className="empty-state-text">Create snippets by selecting portions of the audio waveform or import existing snippets to get started.</p>
                        <div className="empty-state-actions">
                            {isOwner && (
                                <button className="btn btn-primary" onClick={() => setIsAdding(true)}>
                                    <svg viewBox="0 0 24 24">
                                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                                    </svg>
                                    Create Snippet
                                </button>
                            )}
                            <button className="btn btn-secondary" onClick={() => setOpenImport(true)}>
                                <svg viewBox="0 0 24 24">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                </svg>
                                Import Snippets
                            </button>
                        </div>
                    </Box>
                )}
            </Paper>

            {/* Delete confirmation dialog */}
            <ConfirmationDialog
                open={showDeleteDialog}
                title="Delete Snippet?"
                message={`Are you sure you want to delete "${snippetToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                severity="error"
                onConfirm={confirmDeleteSnippet}
                onCancel={() => setShowDeleteDialog(false)}
            />

            {/* Import dialog for both modes */}
            <ImportDialogAPI 
                auditioBackendManager={auditioBackendManager}
                regions={regions}
                wavesurferRef={wavesurferRef}
                regionRef={regionRef}
                openImport={openImport}
                setOpenImport={setOpenImport}
                audioFileId={audioFileId}
                mode={mode}
                onImportComplete={() => {
                    // Refresh snippets after import
                    if (mode === 'youtube') {
                        loadYouTubeSnippets();
                    } else {
                        updateAudioSnippets();
                    }
                    handleSnippetsChange();
                }}
            />

            {/* Export dialog for both modes */}
            <ExportDialog 
                regions={mode === 'audio' ? regions : { current: snippets.reduce((acc: any, snippet) => {
                    acc[snippet.id] = {
                        start: snippet.startTime,
                        end: snippet.endTime,
                        content: { innerText: snippet.name }
                    };
                    return acc;
                }, {}) }}
                openExport={openExport}
                setOpenExport={setOpenExport}
                secondsToHHMMSS={mode === 'audio' ? secondsToHHMMSS : formatTime}
            />
        </Box>
    );
};

export default UnifiedSnippetsView; 